/**
 * SA-717 (email triage spec T29): a router mailbox's routing policy as a
 * form with a Raw toggle. The state is the policy document itself
 * (`routerPolicyModel`): each control edits one leaf, the Raw view edits the
 * same object, and Save hands the document back. The server validates; its
 * one 400 sentence is shown under the field it names (`locatePolicyError`),
 * on the entry when the form has no such field, at the top otherwise.
 * SA-1080 (spec §5.2, §5.3): Save routing adapts the draft to the channel,
 * has the server check it (`onPreview`) and shows what the save will change
 * (`SaveReview`); Save these changes sends it with the policy it was compared
 * to, and a save refused because that policy moved is checked again.
 */
import { Component, useEffect, useRef, useState, type ReactNode } from 'react'
import { Button, Input } from '@/components/ui'
import { RawDefinitionEditor } from '@/pages/WorkflowOverview/process/RawDefinitionEditor'
import { ApiError } from '@/services/apiClient'
import type { MailboxHandler, PolicyPreview } from '@/services/mailboxes'
import { cn } from '@/utils'
import { AddKindForm } from './AddKindForm'
import { policyChanges, type ChangeNames } from './policyChanges'
import { AREA, Field, HandlerFields, SELECT } from './policyEditorParts'
import { addKindPending, rawInvalidPending, leftOutHeading, reviewWords } from './routerPolicyText'
import {
  BUILTIN_IDS, STATUSES, adaptToChannel, isSamePolicy, appendKind, addRule, builtinOf, errorSlot, handlersFor, threadOptionWords, kindsOf, locatePolicyError,
  modelOf, moveRule, removeKind, removeRule, renameKind, ruleFieldsFor, rulesOf, setAttachment, setBuiltin,
  setKind, setModel, setPolicy, setRule, setWhen, splitList, unmatchableKeys, unmatchableLine,
  type KindDoc, type PolicyChannel, type PolicyDoc, type RuleField,
} from './routerPolicyModel'
import { LeftOutList, SaveReview } from './SaveReview'
import { lastChangeLine, type LastChange } from './mailboxesText'

export interface RouterPolicyEditorProps {
  doc: PolicyDoc
  handlers: MailboxHandler[]
  agendas: Array<{ workflow_id: string; name: string }>
  workflows: Array<{ id: string; name: string }>
  /** The newest recorded change to this mailbox's binding (SA-717: every
   * policy change says who), or null / omitted when none is known. */
  lastChange?: LastChange | null
  /** Set only when the mailbox is not a router yet: the sentence that says
   * what Save will change. Shown above Save and in the review, whose Save
   * these changes is the confirmation. */
  switchWarning?: string | null
  /** The server's sentence for a refused save, or null. */
  error: string | null
  saving: boolean
  /** SA-1080: the server's check of a policy (`POST …/policy-preview`): what a save would store and
   * what is stored now. A 400 carries the validator's sentence, placed as a refused save's. */
  onPreview: (doc: PolicyDoc) => Promise<PolicyPreview>
  /** Sends the policy with the stored policy the review compared it to; answers `'policy_changed'`
   * when the server refused it because the stored policy moved since (the editor checks again). */
  onSave: (doc: PolicyDoc, expected: PolicyDoc) => void | Promise<'policy_changed' | void>
  onCancel: () => void
  /** SA-947: the row's channel; the rule fields and labels follow it. */
  channel?: PolicyChannel
  /** Called with whether the draft differs from `doc`, or the Raw view holds text that does not
   * parse, whenever that changes. */
  onDirtyChange?: (dirty: boolean) => void
}

const BUILTIN_TITLE: Record<string, string> = { internal: 'Internal', automated: 'Automated' }
// SA-942 (spec W5): how a typed sender is stored, so the person is not surprised.
const SENDER_HELP =
  'A phone number with its country code, or a WhatsApp id ending in @lid. +44 7700 900123 is stored as 447700900123: the plus, a leading 00, spaces, dashes, dots and brackets are dropped, and digits in other scripts become 0–9. A number without its country code is refused.'

/** A text input that commits on blur, so typing is never interrupted by a
 * re-render (a list's trailing comma, a kind id that renames its key). */
function CommitInput({ id, value, onCommit, placeholder }: {
  id: string; value: string; onCommit: (text: string) => void; placeholder?: string
}) {
  const [text, setText] = useState(value)
  useEffect(() => { setText(value) }, [value])
  return (
    <Input id={id} value={text} placeholder={placeholder}
      onChange={(e) => setText(e.target.value)} onBlur={() => { if (text !== value) onCommit(text) }} />
  )
}

/** Calls the form's render inside its own component so a document the form
 * cannot read throws under the boundary, never in the editor's own render. */
function FormBody({ render }: { render: () => ReactNode }) {
  return <>{render()}</>
}

/** Half-typed YAML in the Raw view can hold shapes the form cannot anticipate.
 * The form becomes one line, and the next change to the document tries it
 * again (SA-717 review; the ProcessDocumentEditor precedent). */
class FormBoundary extends Component<{ doc: unknown; children: ReactNode }, { failed: boolean }> {
  state = { failed: false }
  static getDerivedStateFromError() {
    return { failed: true }
  }
  componentDidUpdate(previous: { doc: unknown }) {
    if (this.state.failed && previous.doc !== this.props.doc) this.setState({ failed: false })
  }
  render() {
    return this.state.failed ? (
      <p className="text-sm text-muted-foreground">
        The form cannot show this document as it stands. Fix it in the Raw view.
      </p>
    ) : this.props.children
  }
}

function numberOrUndefined(text: string): number | undefined {
  return text === '' || Number.isNaN(Number(text)) ? undefined : Number(text)
}

/** What the review shows: the document checked (and sent on Save these changes) and the server's answer. */
interface Review {
  doc: PolicyDoc
  policy: PolicyDoc
  current: PolicyDoc
  leftOut: string[]
  notice: string | null
}

export function RouterPolicyEditor({
  doc, handlers, agendas, workflows, lastChange, switchWarning, error, saving, onPreview, onSave, onCancel,
  channel = 'email', onDirtyChange,
}: RouterPolicyEditorProps) {
  const [draft, setDraft] = useState<PolicyDoc>(doc)
  const [raw, setRaw] = useState(false)
  // Spec §5.7: half-typed Raw text never reaches the draft, yet leaving would lose it.
  const [rawInvalid, setRawInvalid] = useState(false)
  // SA-1398: when the stored policy moves (this editor's save answered, or another admin saved), a
  // draft nobody edited since (the policy it started from, or the one its Save sent) follows it, and
  // anything typed meanwhile is kept. Adjusted during render, so no committed render reads it unsaved.
  const [seed, setSeed] = useState<PolicyDoc>(doc)
  const [sent, setSent] = useState<PolicyDoc | null>(null)
  // SA-1080: the draft followed a moved policy while a save was out. Read when that save answers
  // `policy_changed`: the policy it followed was another admin's, and the person's draft is what they sent.
  const followedWhileSent = useRef(false)
  if (seed !== doc) {
    setSeed(doc)
    if (sent !== null) setSent(null)
    const untouched = isSamePolicy(draft, seed) || (sent !== null && error === null && isSamePolicy(draft, sent))
    if (untouched && !(raw && rawInvalid) && !isSamePolicy(draft, doc)) {
      if (sent !== null) followedWhileSent.current = true
      setDraft(doc)
    }
  }
  const [addOpen, setAddOpen] = useState(false)
  const [addTyped, setAddTyped] = useState(false)
  const focusKind = useRef<string | null>(null)
  const dirty = !isSamePolicy(draft, doc) || (raw && rawInvalid) || addTyped
  useEffect(() => { onDirtyChange?.(dirty) }, [dirty, onDirtyChange])

  // SA-1080 (spec §5.2): Save routing checks, the review shows, Save these changes sends.
  const [phase, setPhase] = useState<'editing' | 'checking' | 'reviewing'>('editing')
  const [review, setReview] = useState<Review | null>(null)
  // A 400 from the check, placed as a refused save's sentence is; a check that failed otherwise.
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [checkError, setCheckError] = useState<string | null>(null)
  // Spec §5.3: what the adaptation left out, shown above the Save row when no review opened.
  const [leftOutNotice, setLeftOutNotice] = useState<string[]>([])
  // A check or a confirmation in flight: a second press waits for it.
  const busy = useRef(false)
  const live = useRef(true)
  useEffect(() => {
    live.current = true
    return () => { live.current = false }
  }, [])
  // A check run after the save answered reads the draft as it is then, edits made while it was in flight included.
  const latestDraft = useRef(draft)
  useEffect(() => { latestDraft.current = draft }, [draft])
  const focusSave = useRef(false)

  const shownError = previewError ?? error
  const slot = shownError ? errorSlot(locatePolicyError(shownError), draft, channel) : null
  const at = (name: string) =>
    slot === name ? <p role="alert" data-slot-error={name} className="mt-1 text-xs text-destructive">{shownError}</p> : null

  // SA-947 (spec P14): what a kind may pick on this channel; a loaded pick stays shown.
  const offered = handlersFor(handlers, channel)
  const kinds = kindsOf(draft)
  const rules = rulesOf(draft)
  const model = modelOf(draft)
  const kindIds = Object.keys(kinds)

  const handlerFields = (
    prefix: string, entry: Partial<KindDoc>, change: (changes: Partial<KindDoc>) => void,
  ) => (
    <HandlerFields prefix={prefix} entry={entry} onChange={change} handlers={handlers} offered={offered}
      agendas={agendas} workflows={workflows} at={at} />
  )

  const thresholdField = (prefix: string, value: number | undefined, change: (v: number | undefined) => void) => (
    <Field label="Threshold" error={at(`${prefix}:min_confidence`)}>
      {(id) => (
        <Input id={id} type="number" min={0} max={1} step={0.05} value={value ?? ''}
          onChange={(e) => change(numberOrUndefined(e.target.value))} />
      )}
    </Field>
  )

  const statusField = (prefix: string, value: string | undefined, change: (v: string) => void) => (
    <Field label="Status after handling" error={at(`${prefix}:status`)}>
      {(id) => (
        <select id={id} className={SELECT} value={value ?? 'active'} onChange={(e) => change(e.target.value)}>
          {STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}
        </select>
      )}
    </Field>
  )

  const names: ChangeNames = {
    handler: (id) => handlers.find((h) => h.id === id)?.label ?? id,
    workflow: (id) => agendas.find((a) => a.workflow_id === id)?.name ?? workflows.find((w) => w.id === id)?.name ?? id,
  }

  /** Adapts `from` to the channel, puts it in the draft, and has the server check it. `carried` is
   * what an earlier adaptation of the same policy left out, so a check run again still says it. */
  const check = async (from: PolicyDoc, notice: string | null, carried: string[]) => {
    if (busy.current) return
    busy.current = true
    const adapted = adaptToChannel(from, channel, handlers)
    const leftOut = [...carried, ...adapted.leftOut.filter((line) => !carried.includes(line))]
    if (adapted.doc !== from) setDraft(adapted.doc)
    setPreviewError(null)
    setCheckError(null)
    setLeftOutNotice([])
    setReview(null)
    setPhase('checking')
    try {
      const answer = await onPreview(adapted.doc)
      if (!live.current) return
      setReview({ doc: adapted.doc, policy: answer.policy, current: answer.current, leftOut, notice })
      setPhase('reviewing')
    } catch (err) {
      if (!live.current) return
      setPhase('editing')
      if (err instanceof ApiError && err.status === 400 && typeof err.detail === 'string') setPreviewError(err.detail)
      else setCheckError(reviewWords.checkFailed)
      setLeftOutNotice(leftOut)
    } finally {
      busy.current = false
    }
  }

  const confirm = async () => {
    if (!review || busy.current) return
    busy.current = true
    const { doc: sending, current, leftOut } = review
    followedWhileSent.current = false
    setSent(sending)
    setReview(null)
    setPhase('editing')
    let answer: 'policy_changed' | void = undefined
    try {
      answer = await onSave(sending, current)
    } finally {
      busy.current = false
    }
    if (answer === 'policy_changed' && live.current) {
      // The send never landed, so the draft is not what is stored; check it again, as it is now. A list read
      // while it was out may have moved the untouched draft to the other admin's policy: the draft is put back
      // to what the person sent, never replaced by what refused it.
      setSent(null)
      const followed = followedWhileSent.current
      followedWhileSent.current = false
      if (followed) setDraft(sending)
      await check(followed ? sending : latestDraft.current, reviewWords.moved, leftOut)
    }
  }

  const reviewLines = review ? policyChanges(review.current, review.policy, names) : []

  const backToEditing = () => {
    focusSave.current = true
    setReview(null)
    setPhase('editing')
  }

  const renderForm = () => (
    <div className="space-y-6">
      <label className="flex items-center gap-2 text-sm text-foreground">
        <input type="checkbox" className="h-4 w-4" checked={draft.approval_default !== false}
          onChange={(e) => setDraft(setPolicy(draft, { approval_default: e.target.checked }))} />
        A person approves every kind unless the kind says otherwise
      </label>
      {at('policy:approval_default')}

      <section className="space-y-3">
        <h4 className="text-sm font-semibold text-foreground">Kinds</h4>
        {kindIds.length === 0 && <p className="text-xs text-muted-foreground">No kinds yet. Every message is captured and waits for a person to give it a kind.</p>}
        {kindIds.map((kindId) => {
          const kind = kinds[kindId]
          const prefix = `kind:${kindId}`
          const change = (changes: Partial<KindDoc>) => setDraft(setKind(draft, kindId, changes))
          return (
            <div key={kindId} role="group" aria-label={`Kind ${kindId}`} tabIndex={-1}
              ref={(el) => { if (el && focusKind.current === kindId) { focusKind.current = null; el.focus() } }}
              className="rounded-md border border-border p-3 space-y-3 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring">
              {at(prefix)}
              <div className="grid grid-cols-1 @md:grid-cols-3 gap-3">
                <Field label="Id" error={at(`${prefix}:id`)}>
                  {(id) => <CommitInput id={id} value={kindId} onCommit={(text) => setDraft(renameKind(draft, kindId, text))} />}
                </Field>
                <Field label="Label" error={at(`${prefix}:label`)}>
                  {(id) => <Input id={id} value={kind.label ?? ''} onChange={(e) => change({ label: e.target.value })} />}
                </Field>
                {handlerFields(prefix, kind, change)}
              </div>
              <Field label="Description (what the classifier reads)" error={at(`${prefix}:description`)}>
                {(id) => <textarea id={id} rows={2} className={AREA} value={kind.description ?? ''} onChange={(e) => change({ description: e.target.value })} />}
              </Field>
              <div className="grid grid-cols-2 @2xl:grid-cols-4 gap-3">
                {thresholdField(prefix, kind.min_confidence, (value) => change({ min_confidence: value }))}
                <Field label="Approval" error={at(`${prefix}:approve`)}>
                  {(id) => (
                    <select id={id} className={SELECT}
                      value={kind.approve === true ? 'yes' : kind.approve === false ? 'no' : 'default'}
                      onChange={(e) => change({ approve: e.target.value === 'yes' ? true : e.target.value === 'no' ? false : null })}>
                      <option value="default">Policy default</option>
                      <option value="yes">A person approves</option>
                      <option value="no">Runs at once</option>
                    </select>
                  )}
                </Field>
                {statusField(prefix, kind.status, (value) => change({ status: value }))}
                <Field label="Reply kind" error={at(`${prefix}:reply_kind`)}>
                  {(id) => (
                    <select id={id} className={SELECT} value={kind.reply_kind ?? ''}
                      onChange={(e) => change({ reply_kind: e.target.value || null })}>
                      <option value="">None</option>
                      {kindIds.filter((other) => other !== kindId).map((other) => <option key={other} value={other}>{other}</option>)}
                    </select>
                  )}
                </Field>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => setDraft(removeKind(draft, kindId))}>Remove kind</Button>
            </div>
          )
        })}
        {addOpen ? (
          <AddKindForm doc={draft} handlers={handlers} offered={offered} agendas={agendas} workflows={workflows}
            onTypedChange={setAddTyped} onCancel={() => { setAddOpen(false); setAddTyped(false) }}
            onAdd={(id, fields) => {
              focusKind.current = id
              setDraft(appendKind(draft, id, fields)); setAddOpen(false); setAddTyped(false)
            }} />
        ) : (
          <Button type="button" variant="secondary" size="sm" onClick={() => setAddOpen(true)}>Add kind</Button>
        )}
      </section>

      <section className="space-y-3">
        <h4 className="text-sm font-semibold text-foreground">Built-in kinds</h4>
        {BUILTIN_IDS.map((builtinId) => {
          const entry = builtinOf(draft, builtinId)
          const prefix = `builtin:${builtinId}`
          const change = (changes: Partial<KindDoc>) => setDraft(setBuiltin(draft, builtinId, changes))
          return (
            <div key={builtinId} role="group" aria-label={`Built-in kind ${builtinId}`} className="rounded-md border border-border p-3 space-y-3">
              <p className="text-sm font-medium text-foreground">{BUILTIN_TITLE[builtinId]}</p>
              {at(prefix)}
              <div className="grid grid-cols-2 @2xl:grid-cols-4 gap-3">
                {handlerFields(prefix, entry, change)}
                {thresholdField(prefix, entry.min_confidence, (value) => change({ min_confidence: value }))}
                <Field label="Approval" error={at(`${prefix}:approve`)}>
                  {(id) => (
                    <select id={id} className={SELECT} value={entry.approve ? 'yes' : 'no'}
                      onChange={(e) => change({ approve: e.target.value === 'yes' })}>
                      <option value="yes">A person approves</option>
                      <option value="no">Runs at once</option>
                    </select>
                  )}
                </Field>
                {statusField(prefix, entry.status, (value) => change({ status: value }))}
              </div>
            </div>
          )
        })}
      </section>

      <section className="space-y-3">
        <h4 className="text-sm font-semibold text-foreground">Rules</h4>
        <p className="text-xs text-muted-foreground">Rules run in order. The first hard rule that matches decides; a hint is shown to the classifier.</p>
        {rules.map((rule, index) => {
          const prefix = `rule:${index}`
          const when = (rule.when ?? {}) as Record<string, unknown>
          const attachment = (when.attachment ?? {}) as Record<string, unknown>
          const list = (key: string) => (Array.isArray(when[key]) ? (when[key] as string[]).join(', ') : '')
          const hard = rule.confidence >= 1
          const yesNo = (key: 'auto_submitted' | 'has_attachments', value: unknown, labels: [string, string]) => (
            <Field key={key} label={key === 'has_attachments' ? 'Has attachments' : 'Auto-submitted'}>
              {(id) => (
                <select id={id} className={SELECT} value={value === true ? 'yes' : value === false ? 'no' : ''}
                  onChange={(e) => setDraft(setWhen(draft, index, key, e.target.value === '' ? undefined : e.target.value === 'yes'))}>
                  <option value="">Any</option>
                  <option value="yes">{labels[0]}</option>
                  <option value="no">{labels[1]}</option>
                </select>
              )}
            </Field>
          )
          // SA-947 (spec P13): one control per rule field; `ruleFieldsFor` says which, in order.
          const condition = (field: RuleField) => {
            switch (field) {
              case 'from':
                return (
                  <Field key={field} label="From" error={at(`${prefix}:from`)}>
                    {(id) => <CommitInput id={id} value={list('from')} placeholder="name@example.com, …" onCommit={(text) => setDraft(setWhen(draft, index, 'from', splitList(text)))} />}
                  </Field>
                )
              case 'from_domain':
                return (
                  <Field key={field} label="From domain" error={at(`${prefix}:from_domain`)}>
                    {(id) => <CommitInput id={id} value={list('from_domain')} placeholder="example.com, …" onCommit={(text) => setDraft(setWhen(draft, index, 'from_domain', splitList(text)))} />}
                  </Field>
                )
              case 'subject':
                return (
                  <Field key={field} label="Subject pattern" error={at(`${prefix}:subject`)}>
                    {(id) => <CommitInput id={id} value={typeof when.subject === 'string' ? when.subject : ''} onCommit={(text) => setDraft(setWhen(draft, index, 'subject', text))} />}
                  </Field>
                )
              case 'sender':
                return (
                  <Field key={field} label="Sender" error={at(`${prefix}:sender`)}>
                    {(id) => (
                      <>
                        <CommitInput id={id} value={list('sender')} placeholder="+44 7700 900123, 12345@lid" onCommit={(text) => setDraft(setWhen(draft, index, 'sender', splitList(text)))} />
                        <p className="mt-1 text-xs text-muted-foreground">{SENDER_HELP}</p>
                      </>
                    )}
                  </Field>
                )
              case 'body':
                return (
                  <Field key={field} label="Body pattern" error={at(`${prefix}:body`)}>
                    {(id) => <CommitInput id={id} value={typeof when.body === 'string' ? when.body : ''} onCommit={(text) => setDraft(setWhen(draft, index, 'body', text))} />}
                  </Field>
                )
              case 'thread':
                return (
                  <Field key={field} label="Thread">
                    {(id) => (
                      <select id={id} className={SELECT} value={typeof when.thread === 'string' ? when.thread : ''}
                        onChange={(e) => setDraft(setWhen(draft, index, 'thread', e.target.value))}>
                        <option value="">Any</option>
                        <option value="new">{threadOptionWords(channel).new}</option>
                        <option value="known">{threadOptionWords(channel).known}</option>
                      </select>
                    )}
                  </Field>
                )
              case 'auto_submitted':
                return yesNo('auto_submitted', when.auto_submitted, ['Machine-generated', 'Not machine-generated'])
              case 'has_attachments':
                return yesNo('has_attachments', when.has_attachments, ['With attachments', 'Without attachments'])
              case 'attachment_mime':
                return (
                  <Field key={field} label="Attachment type">
                    {(id) => <CommitInput id={id} value={typeof attachment.mime === 'string' ? attachment.mime : ''} placeholder="application/pdf" onCommit={(text) => setDraft(setAttachment(draft, index, 'mime', text.trim()))} />}
                  </Field>
                )
              case 'attachment_count':
                return (
                  <Field key={field} label="Attachment count">
                    {(id) => (
                      <Input id={id} type="number" min={0} step={1} value={typeof attachment.count === 'number' ? attachment.count : ''}
                        onChange={(e) => setDraft(setAttachment(draft, index, 'count', numberOrUndefined(e.target.value)))} />
                    )}
                  </Field>
                )
              case 'attachment_filename':
                return (
                  <Field key={field} label="Attachment filename pattern">
                    {(id) => <CommitInput id={id} value={typeof attachment.filename === 'string' ? attachment.filename : ''} onCommit={(text) => setDraft(setAttachment(draft, index, 'filename', text))} />}
                  </Field>
                )
            }
          }
          return (
            <div key={index} role="group" aria-label={`Rule ${rule.id}`} className="rounded-md border border-border p-3 space-y-3">
              {at(prefix)}
              <div className="grid grid-cols-2 @2xl:grid-cols-4 gap-3">
                <Field label="Id" error={at(`${prefix}:id`)}>
                  {(id) => <CommitInput id={id} value={rule.id} onCommit={(text) => setDraft(setRule(draft, index, { id: text.trim() }))} />}
                </Field>
                <Field label="Kind" error={at(`${prefix}:kind`)}>
                  {(id) => (
                    <select id={id} className={SELECT} value={rule.kind} onChange={(e) => setDraft(setRule(draft, index, { kind: e.target.value }))}>
                      {![...kindIds, ...BUILTIN_IDS].includes(rule.kind) && <option value={rule.kind}>{rule.kind}</option>}
                      {[...kindIds, ...BUILTIN_IDS].map((kindId) => <option key={kindId} value={kindId}>{kindId}</option>)}
                    </select>
                  )}
                </Field>
                <Field label="Type">
                  {(id) => (
                    <select id={id} className={SELECT} value={hard ? 'hard' : 'hint'}
                      onChange={(e) => setDraft(setRule(draft, index, { confidence: e.target.value === 'hard' ? 1 : 0.5 }))}>
                      <option value="hard">Hard rule (decides)</option>
                      <option value="hint">Hint (the classifier sees it)</option>
                    </select>
                  )}
                </Field>
                {!hard && (
                  <Field label="Confidence" error={at(`${prefix}:confidence`)}>
                    {(id) => (
                      <Input id={id} type="number" min={0.05} max={0.95} step={0.05} value={rule.confidence}
                        onChange={(e) => setDraft(setRule(draft, index, { confidence: numberOrUndefined(e.target.value) ?? 0.5 }))} />
                    )}
                  </Field>
                )}
                {hard && at(`${prefix}:confidence`)}
              </div>
              <div className="grid grid-cols-1 @md:grid-cols-2 gap-3">
                {ruleFieldsFor(channel).map((field) => condition(field))}
              </div>
              {unmatchableLine(unmatchableKeys(rule, channel)) && (
                <p className="text-xs text-warning-strong">{unmatchableLine(unmatchableKeys(rule, channel))}</p>
              )}
              {at(`${prefix}:thread`)}{at(`${prefix}:auto_submitted`)}{at(`${prefix}:attachment`)}{at(`${prefix}:has_attachments`)}
              <div className="flex items-center gap-2">
                <Button type="button" variant="ghost" size="sm" disabled={index === 0} onClick={() => setDraft(moveRule(draft, index, -1))}>Move up</Button>
                <Button type="button" variant="ghost" size="sm" disabled={index === rules.length - 1} onClick={() => setDraft(moveRule(draft, index, 1))}>Move down</Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => setDraft(removeRule(draft, index))}>Remove rule</Button>
              </div>
            </div>
          )
        })}
        <Button type="button" variant="secondary" size="sm" onClick={() => setDraft(addRule(draft))}>Add rule</Button>
      </section>

      <section className="space-y-3">
        <h4 className="text-sm font-semibold text-foreground">Model</h4>
        {at('model')}
        <label className="flex items-center gap-2 text-sm text-foreground">
          <input type="checkbox" className="h-4 w-4" checked={model.enabled}
            onChange={(e) => setDraft(setModel(draft, { enabled: e.target.checked }))} />
          Classify with the model what no hard rule decides
        </label>
        {at('model:enabled')}
        <Field
          label={channel === 'whatsapp-group' ? 'Instruction (what this group carries, in your words)' : 'Instruction (what this mailbox receives, in your words)'}
          error={at('model:instruction')}>
          {(id) => <textarea id={id} rows={3} className={AREA} value={model.instruction} onChange={(e) => setDraft(setModel(draft, { instruction: e.target.value }))} />}
        </Field>
      </section>
    </div>
  )

  return (
    <div className="@container mt-3 rounded-md border border-border bg-muted/30 p-4 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Routing policy</h3>
          {/* Every saved change is recorded with who made it (SA-717). */}
          {lastChange && <p className="text-xs text-muted-foreground">{lastChangeLine(lastChange)}</p>}
        </div>
        <div role="group" aria-label="View" className="flex items-center gap-1">
          <Button type="button" size="sm" variant={raw ? 'ghost' : 'secondary'} aria-pressed={!raw}
            onClick={() => { setRaw(false); setRawInvalid(false) }}>Form</Button>
          <Button type="button" size="sm" variant={raw ? 'secondary' : 'ghost'} aria-pressed={raw}
            disabled={addTyped} onClick={() => setRaw(true)}>Raw</Button>
        </div>
      </div>
      {/* Spec §5.2: what is being checked, or reviewed, cannot change under it. */}
      <div inert={phase !== 'editing'} className={cn('space-y-4', phase !== 'editing' && 'opacity-60')}>
        {/* In the Raw view no field is on screen: every sentence shows here. */}
        {shownError && (raw || slot === 'top') && (
          <p role="alert" data-slot-error="top" className="text-sm text-destructive">{shownError}</p>
        )}
        {raw ? (
          <RawDefinitionEditor doc={draft} onChange={setDraft} onInvalidChange={setRawInvalid} />
        ) : (
          <FormBoundary doc={draft}><FormBody render={renderForm} /></FormBoundary>
        )}
      </div>
      {phase === 'reviewing' && review ? (
        <SaveReview
          lines={reviewLines}
          leftOut={review.leftOut}
          leftOutHeading={leftOutHeading(channel)}
          switchWarning={switchWarning ?? null}
          notice={review.notice}
          // The button follows the lines: a review never offers a save it does not list, nor hides one it lists.
          // A mailbox that is not a router yet: the switch is the change, even to a router with no kinds.
          nothing={!switchWarning && reviewLines.length === 0}
          onConfirm={() => { void confirm() }}
          onBack={backToEditing}
        />
      ) : (
        <>
          <LeftOutList heading={leftOutHeading(channel)} lines={leftOutNotice} />
          {switchWarning && <p className="text-sm text-warning-strong">{switchWarning}</p>}
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" disabled={saving || addTyped || (raw && rawInvalid) || phase === 'checking'}
              ref={(el) => { if (el && focusSave.current) { focusSave.current = false; el.focus() } }}
              onClick={() => { void check(draft, null, []) }}>
              {saving ? 'Saving…' : phase === 'checking' ? reviewWords.checking : 'Save routing'}
            </Button>
            <Button type="button" variant="ghost" size="sm" disabled={saving || phase === 'checking'}
              onClick={() => { setLeftOutNotice([]); onCancel() }}>Cancel</Button>
          </div>
          {checkError && <p role="alert" className="text-sm text-destructive">{checkError}</p>}
          {addTyped && <p className="text-xs text-muted-foreground">{addKindPending()}</p>}
          {raw && rawInvalid && <p className="text-xs text-muted-foreground">{rawInvalidPending()}</p>}
        </>
      )}
    </div>
  )
}
